import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Calendar.css'; 
import { ReactComponent as Right } from '../../assets/icons/ui/to-right.svg';
import { ReactComponent as Left } from '../../assets/icons/ui/to-left.svg';
import { Programs as ProgramsEN } from '../../data/programs';
import { Programs as ProgramsRU } from '../../data/ruprograms';
import EventCard from '../../components/EventCard/EventCard';
import  EventCardCalendar from './../../components/EventCardCalendar/EventCardCalendar'
import locationIcon from './../../assets/icons/ui/location.svg';
import ClockIcon from './../../assets/icons/ui/clock.svg';
import RightArr from './../../assets/icons/ui/rightarr.svg';
import { useTranslation } from 'react-i18next';

interface Event {
  id: string;
  name: string;
  dateOfEvent: string;
  category?: string;
  programId: string;
  eventMaster: string;
  eventType: string;
  image: string;
  time: string;
  description?: string;
  priceOfEvent: string;
  date: string;
  location: string;
  locationLink: string;
}

const Calendar: React.FC = () => {
   const { t, i18n } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredEvent, setHoveredEvent] = useState<Event | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ left: 0, top: 0, alignLeft: false });
   const Programs = i18n.language === 'ru' ? ProgramsRU : ProgramsEN; 
  
  const navigate = useNavigate();

  
  useEffect(() => {
    const extractedEvents = Programs.flatMap(program =>
      program.events.map(event => ({
        ...event,
        programId: program.id, 
      }))
    );

    const firstDay = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), 1));
    const lastDay = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + 1, 0));

    const filteredEvents = extractedEvents.filter(event => {
      const [year, month, day] = event.dateOfEvent.split('-').map(Number);
      const eventDate = new Date(Date.UTC(year, month - 1, day)); // ✅ Store event in UTC

      // Convert the event date to Dubai Time (UTC+4)
      const dubaiTimeOffset = 4 * 60 * 60 * 1000; // +4 hours in milliseconds
      const eventDateDubai = new Date(eventDate.getTime() + dubaiTimeOffset);

      return (
        eventDateDubai.getUTCFullYear() === currentDate.getUTCFullYear() &&
        eventDateDubai.getUTCMonth() === currentDate.getUTCMonth()
      );
    });

    const sortedEvents = filteredEvents.sort((a, b) => 
      new Date(a.dateOfEvent).getTime() - new Date(b.dateOfEvent).getTime()
    );

    setEvents(sortedEvents);
}, [currentDate, i18n.language]); // ✅ Ensures rerender on month change

  
  const handleEventClick = (programId: string, eventId: string) => {
    navigate(`/program/${programId}/event/${eventId}`);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const cardWidth = 648;  // Adjust based on your actual card width
    const cardHeight = 333; // Adjust based on your actual card height
    const padding = 15; // Space between cursor and card
  
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
  
    let newLeft = e.clientX + padding;
    let newTop = e.clientY + padding;
    let alignLeft = false;
  
    // ✅ If card overflows right edge, move it to the left of cursor
    if (newLeft + cardWidth > windowWidth) {
      newLeft = e.clientX - cardWidth - padding;
      alignLeft = true;
    }
  
    // ✅ If card overflows bottom, move it up
    if (newTop + cardHeight > windowHeight) {
      newTop = windowHeight - cardHeight - padding;
    }
  
    // ✅ If card goes above the top screen, reset to top margin
    if (newTop < 0) {
      newTop = padding;
    }
  
    setHoverPosition({ left: newLeft, top: newTop, alignLeft });
  };
  

  const generateCalendarDays = () => {
    const days = [];
    const year = currentDate.getUTCFullYear();  // ✅ Force UTC
    const month = currentDate.getUTCMonth();    // ✅ Force UTC
    const firstDayOfMonth = new Date(Date.UTC(year, month, 1)); 
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const startDay = firstDayOfMonth.getUTCDay();
  
    for (let i = 0; i < startDay; i++) {
      days.push({ date: null });
    }
  
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(Date.UTC(year, month, i)); // ✅ Ensure all days are in UTC
      days.push({ date });
    }
  
    while (days.length % 7 !== 0) {
      days.push({ date: null });
    }
  
    return days;
  };
  

  const calendarDays = generateCalendarDays();
  const formatDate = (date: Date) => 
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
    .toISOString().split('T')[0];
  

  const weekDays = [
    t('sunday'),
    t('monday'),
    t('tuesday'),
    t('wednesday'),
    t('thursday'),
    t('friday'),
    t('saturday')
  ];
  return (
    <div className="calendar-container">
      <div className="calendar-header"><h1> {t("calendar_title")} </h1></div>
    

      <div className="calendar-header calendar-header-line">
        <div className="calendar-month-year">
        <h2>{new Intl.DateTimeFormat('default', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(currentDate)}</h2>

        </div>
        <div className="calendar-navigation">
        <button className="nav-button" onClick={() => 
  setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
}>
  <Left />
</button>
<button className="nav-button" onClick={() => 
  setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
}>
  <Right />
</button>

        </div>
      </div>

      <div className="calendar-grid-header">
      {weekDays.map((day, index) => (
        <div key={index} className="calendar-day-header">{day}</div>
      ))}
    </div>

      <div className="calendar-grid">
        {calendarDays.map((day, index) => (
          <div key={index} className="calendar-day">
            {day.date && (
              <>
                <div className="day-number">{day.date.getDate()}</div>
                <div className="events">
                  {events
                    .filter(event => formatDate(day.date!) === event.dateOfEvent)
                    .map(event => (
                      <div
                        key={event.id}
                        className="event"
                        onClick={() => handleEventClick(event.programId, event.id)}
                        onMouseEnter={(e) => { setHoveredEvent(event); handleMouseMove(e); }}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={() => setHoveredEvent(null)}
                      >
                         <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={ClockIcon} className="time-icon-calendar" alt="" />
                            <p className='smallest-event-time'>{event.time}</p>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <p className='event-location-calendar'>
                              <img src={locationIcon} alt="Location" className="location-icon-calendar" />
                              {event.location}
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}> 
                          <p className='smallest-event-name-calendar'>{event.name}</p>
                        </div>
                        <img src={RightArr} className="right-icon-calendar" alt="" />
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {hoveredEvent && (
        <div 
          className="event-hover-card" 
          style={{ 
            top: `${hoverPosition.top}px`, 
            left: `${hoverPosition.left}px`,
            transform: hoverPosition.alignLeft ? 'translateX(0%)' : 'translateX(0%)'
          }}
        >
          <EventCardCalendar
            key={hoveredEvent.id}
            eventType={hoveredEvent.eventType}
            image={hoveredEvent.image}
            master={hoveredEvent.eventMaster}
            date={hoveredEvent.date + " @ " + hoveredEvent.time}
            title={hoveredEvent.name}
            description={hoveredEvent.description || "No description"}
            learnMoreLink={`/program/${hoveredEvent.programId}/event/${hoveredEvent.id}`}
            speakersLink={``}
            locationLink={hoveredEvent.locationLink}
            location={hoveredEvent.location} 
            eventPrice={hoveredEvent.priceOfEvent}          />
        </div>
      )}

      {/* Event List Below Calendar */}
      <div className="events-list-container">
       <h2 className="events-list-title">
  {t("events_in")} {new Intl.DateTimeFormat('default', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(currentDate)}
</h2>
 <div className="events-list">
          {events.length > 0 ? (
            events.map(event => (
              <EventCard
                key={event.id}
                eventType={event.eventType}
                master={event.eventMaster}
                image={event.image}
                date={event.date}
                title={event.name}
                description={event.description || 'No description available'}
                learnMoreLink={`/program/${event.programId}/event/${event.id}`}
                locationLink={event.locationLink}
                location={event.location} speakersLink={''}              />
            ))
          ) : (
            <p>No events available for this month.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
